import {
  ClassesBody,
  CourtsBody,
  FinancialBody,
  Heatmap,
  InstructorsBody,
  MoneyBars,
  MoneySeries,
  OverviewBody,
  PackagesBody,
  TournamentsBody,
} from 'modules/arena/reports/model/bodies';
import { addDays, daysInclusive, formatDate, formatDayMonth, weekdayOf, WEEKDAY_SHORT } from 'modules/arena/reports/model/dates';
import {
  CategoryBar,
  CellValue,
  DateBasis,
  Grouping,
  IsoDate,
  Metric,
  Polarity,
  ReportFilters,
  ReportPermissions,
  SeriesPoint,
  SortOption,
  StatusValue,
  TableColumn,
  TablePage,
  TableQuery,
  TableRow,
  ValueUnit,
} from 'modules/arena/reports/model/types';
import {
  ClassSession,
  Contract,
  DemoDb,
  eachDate,
  MIN_RATING_SAMPLE,
  Order,
  PaymentStatus,
  Product,
  RegistrationStatus,
  rng,
} from 'modules/arena/reports/source/demo/db';

// "Backend" de demonstração: filtra, agrega e devolve tudo pronto para a tela formatar.
// Totais são do conjunto filtrado inteiro; percentuais são agregados (não média de turmas).

export const PAGE_SIZE = 10;

export interface AggregateContext {
  db: DemoDb;
  filters: ReportFilters;
  permissions: ReportPermissions;
  today: IsoDate;
}

interface Range {
  start: IsoDate;
  end: IsoDate;
}

// ---------- utilitários ----------

export function previousRange({ start, end }: Range): Range {
  const days = daysInclusive(start, end);
  return { start: addDays(start, -days), end: addDays(start, -1) };
}

function inRange(date: IsoDate | null, range: Range): boolean {
  return date !== null && date >= range.start && date <= range.end;
}

function sum<T>(items: T[], pick: (item: T) => number): number {
  return items.reduce((total, item) => total + pick(item), 0);
}

function pct(part: number, whole: number): number | null {
  return whole > 0 ? (part / whole) * 100 : null;
}

function allowedUnits(ctx: AggregateContext): string[] {
  const selected = ctx.filters.common.unitIds;
  return selected.length ? selected.filter((id) => ctx.permissions.unitIds.includes(id)) : ctx.permissions.unitIds;
}

function matchesCommon(ctx: AggregateContext, unitId: string, sports: string[] | string): boolean {
  const sportList = Array.isArray(sports) ? sports : [sports];
  const { sport } = ctx.filters.common;
  return allowedUnits(ctx).includes(unitId) && (!sport || sportList.includes(sport));
}

function spec(ctx: AggregateContext, key: string): string {
  return ctx.filters.specific[key] ?? '';
}

function compareOn(ctx: AggregateContext): boolean {
  return ctx.filters.common.compare;
}

function metric(
  ctx: AggregateContext,
  base: { id: string; label: string; help: string; unit: ValueUnit; polarity: Polarity; dateBasis?: DateBasis },
  current: number | null,
  previous: number | null | undefined,
  extras: Partial<Metric> = {}
): Metric {
  let comparison: Metric['comparison'] = null;
  if (compareOn(ctx) && previous !== undefined) {
    comparison = {
      previousValue: previous,
      deltaPct: previous && current !== null ? ((current - previous) / Math.abs(previous)) * 100 : null,
    };
  }
  return { ...base, value: current, comparison, ...extras };
}

function status(label: string, tone: StatusValue['tone']): StatusValue {
  return { label, tone };
}

function paginate(
  columns: TableColumn[],
  rows: TableRow[],
  query: TableQuery,
  extras: { totals?: Record<string, CellValue>; sortOptions?: SortOption[]; emptyMessage?: string } = {}
): TablePage {
  const page = Math.max(1, query.page);
  return {
    columns,
    rows: rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    page,
    pageSize: PAGE_SIZE,
    totalRows: rows.length,
    sort: query.sort,
    ...extras,
  };
}

// ---------- agrupamento por dia/semana/mês ----------

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function bucketOf(date: IsoDate, grouping: Grouping): { key: string; label: string } {
  if (grouping === 'mes') return { key: date.slice(0, 7), label: `${MONTHS[Number(date.slice(5, 7)) - 1]}/${date.slice(2, 4)}` };
  if (grouping === 'semana') {
    const monday = addDays(date, -((weekdayOf(date) + 6) % 7));
    return { key: monday, label: `Sem ${formatDayMonth(monday)}` };
  }
  return { key: date, label: formatDayMonth(date) };
}

function buckets(range: Range, grouping: Grouping): { key: string; label: string }[] {
  const seen = new Map<string, string>();
  eachDate(range.start, range.end).forEach((date) => {
    const bucket = bucketOf(date, grouping);
    if (!seen.has(bucket.key)) seen.set(bucket.key, bucket.label);
  });
  return Array.from(seen, ([key, label]) => ({ key, label }));
}

function series<T>(items: T[], dateOf: (item: T) => IsoDate | null, valueOf: (item: T) => number, range: Range, grouping: Grouping): SeriesPoint[] {
  const totals = new Map<string, number>();
  items.forEach((item) => {
    const date = dateOf(item);
    if (!inRange(date, range)) return;
    const key = bucketOf(date!, grouping).key;
    totals.set(key, (totals.get(key) ?? 0) + valueOf(item));
  });
  return buckets(range, grouping).map((bucket) => ({ label: bucket.label, value: totals.get(bucket.key) ?? 0 }));
}

// ---------- financeiro ----------

const PRODUCT_LABEL: Record<Product, string> = {
  reserva: 'Reservas de quadra',
  'aula-avulsa': 'Aulas avulsas',
  pacote: 'Pacotes mensais',
  torneio: 'Inscrições em torneios',
};

const STATUS_VIEW: Record<PaymentStatus, StatusValue> = {
  pago: status('Pago', 'success'),
  parcial: status('Parcialmente pago', 'warning'),
  pendente: status('A receber', 'accent'),
  vencido: status('Vencido', 'danger'),
  cancelado: status('Cancelado', 'neutral'),
  'reembolso-solicitado': status('Reembolso solicitado', 'warning'),
  reembolsado: status('Reembolsado', 'neutral'),
};

const METHOD_LABEL = { pix: 'Pix', cartao: 'Cartão de crédito' };

function charged(order: Order): number {
  return order.grossCents - order.discountCents;
}

/** Taxas proporcionais ao que foi efetivamente recebido. */
function feesOnReceived(order: Order): { platform: number; processorArena: number; processorClient: number } {
  const share = charged(order) ? order.paidCents / charged(order) : 0;
  const processor = Math.round(order.processorFeeCents * share);
  return {
    platform: Math.round(order.platformFeeCents * share),
    processorArena: order.processorFeePaidByClient ? 0 : processor,
    processorClient: order.processorFeePaidByClient ? processor : 0,
  };
}

/** Líquido da arena sobre o recebido: recebido − taxas que cabem à arena − reembolsos concluídos. */
function netReceived(order: Order): number {
  if (!order.paidCents) return 0;
  const fees = feesOnReceived(order);
  return order.paidCents - fees.platform - fees.processorArena - order.refundedCents;
}

function orderDate(order: Order, basis: DateBasis): IsoDate | null {
  switch (basis) {
    case 'recebimento':
      return order.paidDate;
    case 'vencimento':
      return order.dueDate;
    case 'repasse':
      return order.payoutDate;
    default:
      return order.saleDate;
  }
}

function filteredOrders(ctx: AggregateContext, withSpecific: boolean): Order[] {
  return ctx.db.orders.filter(
    (order) =>
      matchesCommon(ctx, order.unitId, order.sport) &&
      (!withSpecific ||
        ((!spec(ctx, 'product') || order.product === spec(ctx, 'product')) &&
          (!spec(ctx, 'paymentStatus') || order.status === spec(ctx, 'paymentStatus')) &&
          (!spec(ctx, 'paymentMethod') || order.method === spec(ctx, 'paymentMethod'))))
  );
}

function salesValue(orders: Order[], range: Range) {
  return sum(orders.filter((o) => inRange(o.saleDate, range) && o.status !== 'cancelado'), charged);
}
function receivedGross(orders: Order[], range: Range) {
  return sum(orders.filter((o) => inRange(o.paidDate, range)), (o) => o.paidCents);
}
function receivedNet(orders: Order[], range: Range) {
  return sum(orders.filter((o) => inRange(o.paidDate, range)), netReceived);
}
function receivable(orders: Order[], range: Range, today: IsoDate) {
  return sum(
    orders.filter((o) => (o.status === 'pendente' || o.status === 'parcial') && o.dueDate >= today && o.dueDate >= range.start),
    (o) => charged(o) - o.paidCents
  );
}
function overdue(orders: Order[], range: Range, today: IsoDate) {
  return sum(
    orders.filter((o) => (o.status === 'vencido' || o.status === 'parcial') && o.dueDate < today && inRange(o.dueDate, range)),
    (o) => charged(o) - o.paidCents
  );
}

function moneySeries(orders: Order[], basis: DateBasis, ctx: AggregateContext, range: Range): MoneySeries {
  const grouping = ctx.filters.common.grouping;
  const dateOf = (order: Order) => orderDate(order, basis);
  const grossOf = (order: Order) => (basis === 'venda' || basis === 'vencimento' ? charged(order) : order.paidCents);
  const prev = previousRange(range);
  return {
    basis,
    bruto: series(orders, dateOf, grossOf, range, grouping),
    liquido: series(orders, dateOf, netReceived, range, grouping),
    previous: compareOn(ctx)
      ? { bruto: series(orders, dateOf, grossOf, prev, grouping), liquido: series(orders, dateOf, netReceived, prev, grouping) }
      : null,
  };
}

function moneyBars(orders: Order[], range: Range, keyOf: (order: Order) => string, labelOf: (key: string) => string): MoneyBars {
  const gross = new Map<string, number>();
  const net = new Map<string, number>();
  orders
    .filter((order) => inRange(order.paidDate, range))
    .forEach((order) => {
      const key = keyOf(order);
      gross.set(key, (gross.get(key) ?? 0) + order.paidCents);
      net.set(key, (net.get(key) ?? 0) + netReceived(order));
    });
  const toBars = (map: Map<string, number>): CategoryBar[] =>
    Array.from(map, ([id, value]) => ({ id, label: labelOf(id), value })).sort((a, b) => b.value - a.value);
  return { bruto: toBars(gross), liquido: toBars(net) };
}

// ---------- aulas ----------

function filteredSessions(ctx: AggregateContext, withSpecific: boolean): (ClassSession & { hour: number })[] {
  const groups = new Map(ctx.db.groups.map((group) => [group.id, group]));
  return ctx.db.sessions
    .map((session) => ({ ...session, hour: groups.get(session.groupId)!.hour }))
    .filter((session) => {
      const group = groups.get(session.groupId)!;
      if (!matchesCommon(ctx, group.unitId, group.sport)) return false;
      if (!withSpecific) return true;
      const band = spec(ctx, 'timeBand');
      const hourBand = session.hour < 12 ? 'manha' : session.hour < 17 ? 'tarde' : 'noite';
      return (
        (!spec(ctx, 'instructorId') || group.instructorId === spec(ctx, 'instructorId')) &&
        (!spec(ctx, 'classGroupId') || group.id === spec(ctx, 'classGroupId')) &&
        (!spec(ctx, 'level') || group.level === spec(ctx, 'level')) &&
        (!spec(ctx, 'weekday') || String(weekdayOf(session.date)) === spec(ctx, 'weekday')) &&
        (!band || band === hourBand)
      );
    });
}

function classStats(sessions: ClassSession[], range: Range) {
  const inPeriod = sessions.filter((s) => inRange(s.date, range) && s.status !== 'agendada');
  const realized = inPeriod.filter((s) => s.status === 'realizada');
  const tracked = realized.filter((s) => s.waitlist !== null);
  return {
    scheduled: inPeriod.length,
    realized: realized.length,
    cancelled: inPeriod.filter((s) => s.status === 'cancelada').length,
    rescheduled: inPeriod.filter((s) => s.status === 'remarcada').length,
    confirmed: sum(realized, (s) => s.confirmed),
    present: sum(realized, (s) => s.present ?? 0),
    absent: sum(realized, (s) => s.absent ?? 0),
    capacity: sum(realized, (s) => s.capacity),
    waitlist: tracked.length ? sum(tracked, (s) => s.waitlist ?? 0) : null,
    students: new Set(realized.flatMap((s) => s.studentIds)).size,
  };
}

// ---------- quadras ----------

type SlotKind = 'confirmada' | 'temporaria' | 'aula' | 'torneio' | 'livre' | 'bloqueado' | 'fechado';

interface Slot {
  courtId: string;
  date: IsoDate;
  hour: number;
  kind: SlotKind;
  used: boolean;
  cancelled: boolean;
}

function courtSlots(ctx: AggregateContext, range: Range, withSpecific: boolean): Slot[] {
  const { db, today } = ctx;
  const slots: Slot[] = [];
  const courts = db.courts.filter(
    (court) => matchesCommon(ctx, court.unitId, court.sports) && (!withSpecific || !spec(ctx, 'courtId') || court.id === spec(ctx, 'courtId'))
  );
  const tournamentDays = new Map(db.tournaments.map((t) => [`${t.unitId}|${t.date}`, true]));
  eachDate(range.start, range.end).forEach((date) => {
    const weekday = weekdayOf(date);
    if (withSpecific && spec(ctx, 'weekday') && String(weekday) !== spec(ctx, 'weekday')) return;
    courts.forEach((court) => {
      for (let hour = 6; hour < 23; hour++) {
        const band = hour < 12 ? 'manha' : hour < 17 ? 'tarde' : 'noite';
        if (withSpecific && spec(ctx, 'timeBand') && spec(ctx, 'timeBand') !== band) continue;
        const random = rng(`${db.arenaKey}|slot|${court.id}|${date}|${hour}`);
        const open = db.openDays.includes(weekday) && hour >= db.openHour && hour < db.closeHour;
        let kind: SlotKind = 'livre';
        if (!open) kind = 'fechado';
        else if (random() < 0.03) kind = 'bloqueado';
        else if (tournamentDays.has(`${court.unitId}|${date}`) && hour >= 8 && hour < 18) kind = 'torneio';
        else {
          const peak = hour >= 17 && hour < 22 ? 0.85 : weekday === 0 || weekday === 6 ? 0.7 : hour < 9 ? 0.45 : 0.25;
          const roll = random();
          if (roll < 0.08) kind = 'aula';
          else if (roll < 0.08 + peak) kind = 'confirmada';
          else if (date >= today && roll < 0.08 + peak + 0.06) kind = 'temporaria';
        }
        const cancelled = kind === 'livre' && random() < 0.05;
        slots.push({ courtId: court.id, date, hour, kind, used: kind === 'confirmada' && date < today && random() < 0.92, cancelled });
      }
    });
  });
  return slots;
}

function courtStats(slots: Slot[]) {
  const available = slots.filter((s) => s.kind !== 'fechado' && s.kind !== 'bloqueado').length;
  const occupied = slots.filter((s) => s.kind === 'confirmada' || s.kind === 'aula' || s.kind === 'torneio').length;
  return {
    available,
    occupied,
    confirmed: slots.filter((s) => s.kind === 'confirmada').length,
    temporary: slots.filter((s) => s.kind === 'temporaria').length,
    classes: slots.filter((s) => s.kind === 'aula').length,
    tournaments: slots.filter((s) => s.kind === 'torneio').length,
    blocked: slots.filter((s) => s.kind === 'bloqueado').length,
    used: slots.filter((s) => s.used).length,
    cancelled: slots.filter((s) => s.cancelled).length,
    occupancy: pct(occupied, available),
  };
}

// =========================================================================================
// Áreas
// =========================================================================================

export function hasOrders(ctx: AggregateContext, range: Range): boolean {
  return ctx.db.orders.some((order) => inRange(order.saleDate, range) || inRange(order.paidDate, range));
}

export function buildOverview(ctx: AggregateContext, range: Range, query: TableQuery): OverviewBody {
  const prev = previousRange(range);
  const orders = filteredOrders(ctx, false);
  const sessions = filteredSessions(ctx, false);
  const cs = classStats(sessions, range);
  const cp = classStats(sessions, prev);
  const reservationCancels = (r: Range) => orders.filter((o) => o.status === 'cancelado' && inRange(o.saleDate, r)).length;

  const metrics: Metric[] = [
    metric(ctx, { id: 'vendido', label: 'Valor vendido', help: 'Soma dos pedidos vendidos no período (já com descontos), pagos ou não. Não é o mesmo que valor recebido.', unit: 'money', polarity: 'up', dateBasis: 'venda' }, salesValue(orders, range), salesValue(orders, prev), { drillDown: { area: 'financeiro', specific: { dateBasis: 'venda' } } }),
    metric(ctx, { id: 'recebido-bruto', label: 'Recebimento bruto da arena', help: 'Dinheiro efetivamente recebido no período, antes das taxas.', unit: 'money', polarity: 'up', dateBasis: 'recebimento' }, receivedGross(orders, range), receivedGross(orders, prev), { drillDown: { area: 'financeiro', specific: { dateBasis: 'recebimento' } } }),
    metric(ctx, { id: 'liquido', label: 'Valor líquido da arena', help: 'Recebido menos as taxas que cabem à arena e os reembolsos concluídos. Não é lucro: custos da arena não entram aqui.', unit: 'money', polarity: 'up', dateBasis: 'recebimento' }, receivedNet(orders, range), receivedNet(orders, prev), { drillDown: { area: 'financeiro', specific: { dateBasis: 'recebimento' } } }),
    metric(ctx, { id: 'a-receber', label: 'A receber', help: 'Saldo de pedidos ainda dentro do prazo de pagamento (inclui o restante de pedidos parcialmente pagos).', unit: 'money', polarity: 'neutral', dateBasis: 'vencimento' }, receivable(orders, range, ctx.today), undefined, { drillDown: { area: 'financeiro', specific: { dateBasis: 'vencimento', paymentStatus: 'pendente' } } }),
    metric(ctx, { id: 'vencido', label: 'Vencidos', help: 'Saldo de pedidos com vencimento no período que passou do prazo sem pagamento.', unit: 'money', polarity: 'down', dateBasis: 'vencimento' }, overdue(orders, range, ctx.today), overdue(orders, prev, ctx.today), { drillDown: { area: 'financeiro', specific: { dateBasis: 'vencimento', paymentStatus: 'vencido' } } }),
    metric(ctx, { id: 'aulas-realizadas', label: 'Aulas realizadas', help: 'Aulas que aconteceram no período (sem contar canceladas e remarcadas).', unit: 'count', polarity: 'up', dateBasis: 'aula' }, cs.realized, cp.realized, { drillDown: { area: 'aulas' } }),
    metric(ctx, { id: 'alunos-unicos', label: 'Alunos únicos', help: 'Pessoas diferentes que participaram de ao menos uma aula. Uma pessoa em várias aulas conta uma vez.', unit: 'count', polarity: 'up', dateBasis: 'aula' }, cs.students, cp.students, { drillDown: { area: 'pacotes' } }),
    metric(ctx, { id: 'ocupacao-aulas', label: 'Ocupação das aulas', help: 'Presenças sobre vagas de todas as aulas realizadas (percentual agregado, não média entre turmas).', unit: 'percent', polarity: 'up', dateBasis: 'aula' }, pct(cs.present, cs.capacity), pct(cp.present, cp.capacity), { detail: `${cs.present} de ${cs.capacity} vagas`, drillDown: { area: 'aulas' } }),
    metric(ctx, { id: 'cancelamentos', label: 'Cancelamentos', help: 'Aulas canceladas + pedidos cancelados no período. Aumento aqui é sinal de atenção.', unit: 'count', polarity: 'down', dateBasis: 'misto' as DateBasis }, cs.cancelled + reservationCancels(range), cp.cancelled + reservationCancels(prev), { detail: `${cs.cancelled} aula(s) · ${reservationCancels(range)} pedido(s)`, drillDown: { area: 'quadras' } }),
  ];

  // Sem acesso ao financeiro, a visão geral não leva nenhum valor em dinheiro (nem na tabela).
  const canSeeMoney = ctx.permissions.areas.includes('financeiro');
  const MONEY_KEYS = ['sold', 'gross', 'net'];
  const stripMoney = <T extends Record<string, unknown>>(cells: T) =>
    canSeeMoney ? cells : (Object.fromEntries(Object.entries(cells).filter(([key]) => !MONEY_KEYS.includes(key))) as T);

  const unitLabel = (id: string) => ctx.db.units.find((unit) => unit.id === id)?.label ?? id;
  const columns: TableColumn[] = [
    { key: 'unit', label: 'Unidade', kind: 'text', primary: true },
    { key: 'sold', label: 'Valor vendido', kind: 'money' },
    { key: 'gross', label: 'Recebido (bruto)', kind: 'money' },
    { key: 'net', label: 'Líquido da arena', kind: 'money', primary: true },
    { key: 'classes', label: 'Aulas realizadas', kind: 'count' },
    { key: 'occupancy', label: 'Ocupação das aulas', kind: 'percent' },
    { key: 'cancelled', label: 'Cancelamentos', kind: 'count' },
  ].filter((column) => canSeeMoney || !MONEY_KEYS.includes(column.key)) as TableColumn[];
  const rows: TableRow[] = allowedUnits(ctx).map((unitId) => {
    const unitOrders = orders.filter((order) => order.unitId === unitId);
    const unitSessions = sessions.filter((session) => ctx.db.groups.find((g) => g.id === session.groupId)?.unitId === unitId);
    const stats = classStats(unitSessions, range);
    return {
      id: unitId,
      cells: stripMoney({
        unit: unitLabel(unitId),
        sold: salesValue(unitOrders, range),
        gross: receivedGross(unitOrders, range),
        net: receivedNet(unitOrders, range),
        classes: stats.realized,
        occupancy: pct(stats.present, stats.capacity),
        cancelled: stats.cancelled + unitOrders.filter((o) => o.status === 'cancelado' && inRange(o.saleDate, range)).length,
      }),
    };
  });

  return {
    metrics: canSeeMoney ? metrics : metrics.filter((item) => item.unit !== 'money'),
    series: canSeeMoney ? moneySeries(orders, 'recebimento', ctx, range) : null,
    breakdowns: canSeeMoney
      ? {
          sport: moneyBars(orders, range, (o) => o.sport, (id) => id),
          unit: moneyBars(orders, range, (o) => o.unitId, unitLabel),
          product: moneyBars(orders, range, (o) => o.product, (id) => PRODUCT_LABEL[id as Product]),
        }
      : null,
    table: paginate(columns, rows, query, {
      totals: stripMoney({
        unit: 'Total',
        sold: salesValue(orders, range),
        gross: receivedGross(orders, range),
        net: receivedNet(orders, range),
        classes: cs.realized,
        occupancy: pct(cs.present, cs.capacity),
        cancelled: cs.cancelled + reservationCancels(range),
      }),
    }),
  };
}

// ---------- Financeiro ----------

const FINANCIAL_COLUMNS: TableColumn[] = [
  { key: 'date', label: 'Data de referência', kind: 'date' },
  { key: 'id', label: 'Pedido', kind: 'text', primary: true },
  { key: 'product', label: 'Produto', kind: 'text' },
  { key: 'unit', label: 'Unidade', kind: 'text' },
  { key: 'gross', label: 'Valor bruto', kind: 'money' },
  { key: 'fees', label: 'Taxas da arena', kind: 'money' },
  { key: 'refunds', label: 'Reembolsos', kind: 'money' },
  { key: 'net', label: 'Líquido recebido', kind: 'money', primary: true },
  { key: 'status', label: 'Situação', kind: 'status', primary: true },
  { key: 'method', label: 'Meio de pagamento', kind: 'text' },
];

export function financialRows(ctx: AggregateContext, range: Range): { rows: TableRow[]; orders: Order[]; basis: DateBasis } {
  const basis = (spec(ctx, 'dateBasis') || 'venda') as DateBasis;
  const orders = filteredOrders(ctx, true)
    .filter((order) => inRange(orderDate(order, basis), range))
    .sort((a, b) => (orderDate(b, basis)! > orderDate(a, basis)! ? 1 : -1));
  const unitLabel = (id: string) => ctx.db.units.find((unit) => unit.id === id)?.label ?? id;
  const rows = orders.map((order) => {
    const fees = feesOnReceived(order);
    return {
      id: order.id,
      hasDetail: true,
      cells: {
        date: orderDate(order, basis),
        id: order.id,
        product: PRODUCT_LABEL[order.product],
        unit: unitLabel(order.unitId),
        gross: order.grossCents,
        fees: -(fees.platform + fees.processorArena),
        refunds: order.refundedCents ? -order.refundedCents : 0,
        net: netReceived(order),
        status: STATUS_VIEW[order.status],
        method: METHOD_LABEL[order.method],
      },
    };
  });
  return { rows, orders, basis };
}

export function buildFinancial(ctx: AggregateContext, range: Range, query: TableQuery): FinancialBody {
  const prev = previousRange(range);
  const all = filteredOrders(ctx, true);
  const { rows, orders: inBasis, basis } = financialRows(ctx, range);

  const refundRequested = (os: Order[]) => sum(os.filter((o) => o.status === 'reembolso-solicitado'), charged);
  const refundDone = (os: Order[]) => sum(os, (o) => o.refundedCents);
  const partialBalance = (os: Order[]) => sum(os.filter((o) => o.status === 'parcial'), (o) => charged(o) - o.paidCents);
  const payouts = (os: Order[], r: Range) => sum(os.filter((o) => inRange(o.payoutDate, r)), netReceived);

  const fees = inBasis.map(feesOnReceived);
  const grossBase = sum(inBasis, (o) => o.grossCents);
  const discounts = sum(inBasis, (o) => o.discountCents);
  const notReceived = sum(inBasis, (o) => charged(o) - o.paidCents);
  const platform = sum(fees, (f) => f.platform);
  const processorArena = sum(fees, (f) => f.processorArena);
  const refunds = refundDone(inBasis);

  return {
    groups: [
      {
        id: 'vendas',
        title: 'Vendas',
        description: 'O que foi vendido. Venda não é dinheiro em caixa.',
        metrics: [
          metric(ctx, { id: 'vendido', label: 'Valor vendido', help: 'Pedidos vendidos no período (com descontos), pagos ou não.', unit: 'money', polarity: 'up', dateBasis: 'venda' }, salesValue(all, range), salesValue(all, prev)),
          metric(ctx, { id: 'pedidos', label: 'Pedidos vendidos', help: 'Quantidade de pedidos não cancelados com venda no período.', unit: 'count', polarity: 'up', dateBasis: 'venda' }, all.filter((o) => inRange(o.saleDate, range) && o.status !== 'cancelado').length, all.filter((o) => inRange(o.saleDate, prev) && o.status !== 'cancelado').length),
        ],
      },
      {
        id: 'recebimentos',
        title: 'Recebimentos',
        description: 'Dinheiro que entrou de fato, antes e depois das taxas.',
        metrics: [
          metric(ctx, { id: 'recebido-bruto', label: 'Recebido (bruto)', help: 'Valor pago pelos clientes no período.', unit: 'money', polarity: 'up', dateBasis: 'recebimento' }, receivedGross(all, range), receivedGross(all, prev)),
          metric(ctx, { id: 'liquido', label: 'Líquido da arena', help: 'Recebido − taxas que cabem à arena − reembolsos concluídos. Não é lucro.', unit: 'money', polarity: 'up', dateBasis: 'recebimento' }, receivedNet(all, range), receivedNet(all, prev)),
        ],
      },
      {
        id: 'pendencias',
        title: 'Pendências',
        description: 'Valores ainda não recebidos, separados por prazo.',
        metrics: [
          metric(ctx, { id: 'a-receber', label: 'A receber', help: 'Dentro do prazo de pagamento.', unit: 'money', polarity: 'neutral', dateBasis: 'vencimento' }, receivable(all, range, ctx.today), undefined),
          metric(ctx, { id: 'vencido', label: 'Vencidos', help: 'Passaram do vencimento sem pagamento.', unit: 'money', polarity: 'down', dateBasis: 'vencimento' }, overdue(all, range, ctx.today), overdue(all, prev, ctx.today)),
          metric(ctx, { id: 'saldo-parcial', label: 'Saldo de pedidos parciais', help: 'Parte ainda não paga de pedidos com pagamento parcial (o valor já recebido está em Recebimentos).', unit: 'money', polarity: 'neutral', dateBasis: 'venda' }, partialBalance(all.filter((o) => inRange(o.saleDate, range))), undefined),
        ],
      },
      {
        id: 'reembolsos',
        title: 'Reembolsos',
        description: 'Solicitação não é reembolso concluído: só o concluído sai do líquido.',
        metrics: [
          metric(ctx, { id: 'reembolso-solicitado', label: 'Reembolsos solicitados', help: 'Aguardando confirmação do processador. Ainda não saíram do caixa.', unit: 'money', polarity: 'down', dateBasis: 'venda' }, refundRequested(all.filter((o) => inRange(o.saleDate, range))), refundRequested(all.filter((o) => inRange(o.saleDate, prev)))),
          metric(ctx, { id: 'reembolso-concluido', label: 'Reembolsos concluídos', help: 'Devolvidos ao cliente e confirmados.', unit: 'money', polarity: 'down', dateBasis: 'recebimento' }, refundDone(all.filter((o) => inRange(o.paidDate, range))), refundDone(all.filter((o) => inRange(o.paidDate, prev)))),
        ],
      },
      {
        id: 'repasses',
        title: 'Repasses',
        description: 'Transferências para a conta da arena. Repasse não é receita nova: é o líquido já contado acima chegando ao banco.',
        metrics: [
          metric(ctx, { id: 'repassado', label: 'Repassado à arena', help: 'Valor transferido no período.', unit: 'money', polarity: 'neutral', dateBasis: 'repasse' }, payouts(all, range), payouts(all, prev)),
        ],
      },
    ],
    composition: {
      basis,
      clientPaidFeesCents: sum(fees, (f) => f.processorClient),
      rows: [
        { id: 'bruto', label: 'Valor bruto (preço de tabela)', cents: grossBase, kind: 'base', help: 'Soma dos preços dos pedidos.' },
        { id: 'descontos', label: 'Descontos concedidos', cents: -discounts, kind: 'deduction', help: 'Cupons e descontos dados pela arena.' },
        { id: 'nao-recebido', label: 'Ainda não recebido', cents: -notReceived, kind: 'deduction', help: 'Pendentes, vencidos, saldos parciais e cancelados.' },
        { id: 'plataforma', label: 'Taxa da plataforma', cents: -platform, kind: 'deduction', help: 'Taxa registrada em cada pedido no momento da venda. Cabe à arena.' },
        { id: 'processador', label: 'Taxa do processador (paga pela arena)', cents: -processorArena, kind: 'deduction', help: 'Pix e cartão em que a arena absorve a taxa.' },
        { id: 'reembolsos', label: 'Reembolsos concluídos', cents: -refunds, kind: 'deduction', help: 'Somente devoluções confirmadas.' },
        { id: 'liquido', label: 'Líquido da arena', cents: sum(inBasis, netReceived), kind: 'total', help: 'Não é lucro: custos e despesas da arena não fazem parte destes dados.' },
      ],
    },
    series: moneySeries(all, basis, ctx, range),
    byProduct: moneyBars(inBasis, { start: '0000-01-01', end: '9999-12-31' }, (o) => o.product, (id) => PRODUCT_LABEL[id as Product]),
    byMethod: moneyBars(inBasis, { start: '0000-01-01', end: '9999-12-31' }, (o) => o.method, (id) => METHOD_LABEL[id as 'pix' | 'cartao']),
    table: paginate(FINANCIAL_COLUMNS, rows, query, {
      totals: {
        date: 'Total do período',
        gross: grossBase,
        fees: -(platform + processorArena),
        refunds: refunds ? -refunds : 0,
        net: sum(inBasis, netReceived),
      },
      emptyMessage: 'Nenhum pedido com esses filtros na data de referência escolhida.',
    }),
  };
}

export function financialDetail(ctx: AggregateContext, orderId: string) {
  const order = ctx.db.orders.find((item) => item.id === orderId);
  if (!order || !ctx.permissions.unitIds.includes(order.unitId)) return null;
  return { order, fees: feesOnReceived(order), charged: charged(order), net: netReceived(order) };
}

// ---------- Aulas ----------

const CLASS_SORTS: SortOption[] = [
  { id: 'ocupacao', label: 'Maior ocupação (%)' },
  { id: 'participantes', label: 'Mais participantes' },
  { id: 'espera', label: 'Maior espera' },
];

export function classRows(ctx: AggregateContext, range: Range, sort: string): TableRow[] {
  const sessions = filteredSessions(ctx, true);
  const instructors = new Map(ctx.db.instructors.map((item) => [item.id, item.label]));
  const units = new Map(ctx.db.units.map((item) => [item.id, item.label]));
  const rows = ctx.db.groups
    .map((group) => {
      const stats = classStats(
        sessions.filter((s) => s.groupId === group.id),
        range
      );
      if (!stats.scheduled) return null;
      const occupancy = pct(stats.present, stats.capacity);
      return {
        sortValues: { ocupacao: occupancy ?? -1, participantes: stats.present, espera: stats.waitlist ?? -1 },
        row: {
          id: group.id,
          cells: {
            group: group.label,
            sport: group.sport,
            level: group.level,
            instructor: instructors.get(group.instructorId) ?? '—',
            unit: units.get(group.unitId) ?? '—',
            schedule: `${group.weekdays.map((d) => WEEKDAY_SHORT[d]).join(' e ')} · ${String(group.hour).padStart(2, '0')}h`,
            realized: stats.realized,
            occupancy: stats.realized
              ? { value: Math.round((stats.present / stats.realized) * 10) / 10, capacity: group.capacity, pct: occupancy ?? 0, unit: 'por aula' }
              : null,
            participants: stats.present,
            waitlist: stats.waitlist,
            status: stats.cancelled ? status(`${stats.cancelled} cancelada(s)`, 'warning') : status('Regular', 'success'),
          } as Record<string, CellValue>,
        },
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const key = (sort || 'ocupacao') as 'ocupacao' | 'participantes' | 'espera';
  return rows.sort((a, b) => b.sortValues[key] - a.sortValues[key]).map((item) => item.row);
}

const CLASS_COLUMNS: TableColumn[] = [
  { key: 'group', label: 'Turma', kind: 'text', primary: true },
  { key: 'sport', label: 'Esporte', kind: 'text' },
  { key: 'level', label: 'Nível', kind: 'text' },
  { key: 'instructor', label: 'Professor', kind: 'text' },
  { key: 'unit', label: 'Unidade', kind: 'text' },
  { key: 'schedule', label: 'Dia e horário', kind: 'text' },
  { key: 'realized', label: 'Aulas realizadas', kind: 'count' },
  { key: 'occupancy', label: 'Presentes / capacidade', kind: 'ratio', primary: true },
  { key: 'participants', label: 'Participações', kind: 'count' },
  { key: 'waitlist', label: 'Espera', kind: 'count' },
  { key: 'status', label: 'Situação', kind: 'status' },
];

export function buildClasses(ctx: AggregateContext, range: Range, query: TableQuery): ClassesBody {
  const prev = previousRange(range);
  const sessions = filteredSessions(ctx, true);
  const s = classStats(sessions, range);
  const p = classStats(sessions, prev);
  const base = { dateBasis: 'aula' as DateBasis };

  const metrics: Metric[] = [
    metric(ctx, { ...base, id: 'programadas', label: 'Aulas programadas', help: 'Aulas previstas no período (realizadas, canceladas e remarcadas).', unit: 'count', polarity: 'neutral' }, s.scheduled, p.scheduled),
    metric(ctx, { ...base, id: 'realizadas', label: 'Realizadas', help: 'Aulas que aconteceram.', unit: 'count', polarity: 'up' }, s.realized, p.realized),
    metric(ctx, { ...base, id: 'canceladas', label: 'Canceladas', help: 'Aulas canceladas pela arena ou por falta de quórum.', unit: 'count', polarity: 'down' }, s.cancelled, p.cancelled),
    metric(ctx, { ...base, id: 'remarcadas', label: 'Remarcadas', help: 'Aulas movidas para outra data.', unit: 'count', polarity: 'neutral' }, s.rescheduled, p.rescheduled),
    metric(ctx, { ...base, id: 'confirmados', label: 'Alunos confirmados', help: 'Confirmações nas aulas realizadas.', unit: 'count', polarity: 'up' }, s.confirmed, p.confirmed),
    metric(ctx, { ...base, id: 'presencas', label: 'Presenças', help: 'Alunos presentes nas aulas realizadas.', unit: 'count', polarity: 'up' }, s.present, p.present),
    metric(ctx, { ...base, id: 'faltas', label: 'Faltas', help: 'Confirmados que não compareceram. Aumento é sinal de atenção.', unit: 'count', polarity: 'down' }, s.absent, p.absent),
    metric(ctx, { ...base, id: 'ocupacao', label: 'Ocupação', help: 'Presenças sobre vagas de todas as aulas realizadas (agregado, não média entre turmas).', unit: 'percent', polarity: 'up' }, pct(s.present, s.capacity), pct(p.present, p.capacity), { detail: `${s.present} de ${s.capacity} vagas` }),
    metric(ctx, { ...base, id: 'espera', label: 'Em espera', help: 'Pessoas na fila de espera. Turmas sem registro de fila não entram.', unit: 'count', polarity: 'neutral' }, s.waitlist, p.waitlist, s.waitlist === null ? { unavailableReason: 'As turmas filtradas não registram fila de espera.' } : {}),
  ];

  // Próximas aulas: separadas do período (ainda não há presença ou falta).
  const upcomingRange = { start: addDays(ctx.today, 1), end: addDays(ctx.today, 7) };
  const upcoming = sessions.filter((session) => inRange(session.date, upcomingRange)).sort((a, b) => (a.date > b.date ? 1 : -1));
  const groups = new Map(ctx.db.groups.map((group) => [group.id, group]));
  const upcomingRows: TableRow[] = upcoming.map((session) => ({
    id: session.id,
    cells: {
      date: session.date,
      group: groups.get(session.groupId)!.label,
      hour: `${String(session.hour).padStart(2, '0')}h`,
      confirmed: { value: session.confirmed, capacity: session.capacity, pct: (session.confirmed / session.capacity) * 100, unit: 'confirmados' },
      attendance: status('Ainda não ocorreu', 'neutral'),
    },
  }));

  // Ocupação por dia da semana × faixa de horário.
  const bands = [
    { id: 'manha', label: 'Manhã', test: (h: number) => h < 12 },
    { id: 'tarde', label: 'Tarde', test: (h: number) => h >= 12 && h < 17 },
    { id: 'noite', label: 'Noite', test: (h: number) => h >= 17 },
  ];
  const weekdays = [1, 2, 3, 4, 5, 6, 0];
  const realized = sessions.filter((session) => inRange(session.date, range) && session.status === 'realizada');
  const heatmap: Heatmap = {
    rows: weekdays.map((d) => WEEKDAY_SHORT[d]),
    columns: bands.map((band) => band.label),
    cells: weekdays.map((day) =>
      bands.map((band) => {
        const cell = realized.filter((session) => weekdayOf(session.date) === day && band.test(session.hour));
        const present = sum(cell, (session) => session.present ?? 0);
        const capacity = sum(cell, (session) => session.capacity);
        return capacity
          ? { value: pct(present, capacity), state: 'ok' as const, detail: `${present} de ${capacity} vagas` }
          : { value: null, state: 'empty' as const, detail: 'Sem aulas neste horário' };
      })
    ),
  };

  return {
    metrics,
    upcoming: {
      metrics: [
        metric(ctx, { id: 'proximas', label: 'Próximas aulas (7 dias)', help: 'Aulas agendadas após hoje. Ainda não têm presença registrada.', unit: 'count', polarity: 'neutral', dateBasis: 'aula' }, upcoming.length, undefined),
        metric(ctx, { id: 'confirmados-futuro', label: 'Confirmações futuras', help: 'Alunos já confirmados nas próximas aulas.', unit: 'count', polarity: 'neutral', dateBasis: 'aula' }, sum(upcoming, (session) => session.confirmed), undefined),
      ],
      table: paginate(
        [
          { key: 'date', label: 'Data', kind: 'date', primary: true },
          { key: 'group', label: 'Turma', kind: 'text', primary: true },
          { key: 'hour', label: 'Horário', kind: 'text' },
          { key: 'confirmed', label: 'Confirmados / vagas', kind: 'ratio' },
          { key: 'attendance', label: 'Presença', kind: 'status' },
        ],
        upcomingRows,
        { page: 1 },
        { emptyMessage: 'Nenhuma aula agendada para os próximos 7 dias com esses filtros.' }
      ),
    },
    heatmap,
    table: paginate(CLASS_COLUMNS, classRows(ctx, range, query.sort ?? 'ocupacao'), { ...query, sort: query.sort ?? 'ocupacao' }, {
      sortOptions: CLASS_SORTS,
      totals: {
        group: 'Todas as turmas filtradas',
        realized: s.realized,
        occupancy: s.realized ? { value: s.present, capacity: s.capacity, pct: pct(s.present, s.capacity) ?? 0, unit: 'no período' } : null,
        participants: s.present,
        waitlist: s.waitlist,
      },
      emptyMessage: 'Nenhuma turma com aulas nesse período e filtros.',
    }),
  };
}

// ---------- Professores ----------

const INSTRUCTOR_SORTS: SortOption[] = [
  { id: 'nota', label: 'Melhor avaliado (nota média)' },
  { id: 'avaliacoes', label: 'Mais avaliado (quantidade)' },
  { id: 'atividade', label: 'Maior atividade (aulas)' },
];

export function instructorRows(ctx: AggregateContext, range: Range, sort: string): TableRow[] {
  const sessions = filteredSessions(ctx, false);
  const sport = ctx.filters.common.sport;
  const data = ctx.db.instructors
    .filter((instructor) => !sport || instructor.sports.includes(sport))
    .map((instructor) => {
      const ratings = ctx.db.ratings.filter((r) => r.target === 'professor' && r.instructorId === instructor.id && inRange(r.date, range));
      const groupIds = ctx.db.groups.filter((g) => g.instructorId === instructor.id).map((g) => g.id);
      const stats = classStats(sessions.filter((session) => groupIds.includes(session.groupId)), range);
      const average = ratings.length ? sum(ratings, (r) => r.score) / ratings.length : null;
      const eligible = ratings.length >= MIN_RATING_SAMPLE;
      return { instructor, ratings, average, eligible, stats };
    });

  const key = sort || 'nota';
  data.sort((a, b) => {
    if (key === 'avaliacoes') return b.ratings.length - a.ratings.length;
    if (key === 'atividade') return b.stats.realized - a.stats.realized || b.stats.students - a.stats.students;
    // Nota: elegíveis primeiro (por média), depois amostra insuficiente, por último sem avaliações.
    const rank = (item: typeof a) => (item.average === null ? 2 : item.eligible ? 0 : 1);
    return rank(a) - rank(b) || (b.average ?? 0) - (a.average ?? 0);
  });

  let position = 0;
  return data.map((item) => {
    const ranked = key !== 'nota' || item.eligible;
    if (ranked) position += 1;
    return {
      id: item.instructor.id,
      cells: {
        position: ranked ? `${position}º` : '—',
        name: item.instructor.label,
        sports: item.instructor.sports.join(', '),
        average: item.average,
        count: item.ratings.length,
        raters: new Set(item.ratings.map((r) => r.studentId)).size,
        sample: item.average === null
          ? status('Ainda sem avaliações', 'neutral')
          : item.eligible
            ? status('Elegível ao ranking', 'success')
            : status(`Amostra insuficiente (mín. ${MIN_RATING_SAMPLE})`, 'warning'),
        classes: item.stats.realized,
        students: item.stats.students,
      },
    };
  });
}

export function buildInstructors(ctx: AggregateContext, range: Range, query: TableQuery): InstructorsBody {
  const structure = ctx.db.ratings.filter((r) => r.target === 'estrutura' && inRange(r.date, range));
  const comments = ctx.permissions.canSeeComments
    ? ctx.db.ratings
        .filter((r) => r.comment && inRange(r.date, range))
        .sort((a, b) => (a.date < b.date ? 1 : -1))
        .slice(0, 6)
        .map((r) => ({
          id: r.id,
          date: r.date,
          instructor: r.instructorId ? ctx.db.instructors.find((i) => i.id === r.instructorId)?.label ?? null : null,
          score: r.score,
          text: r.comment!,
          author: ctx.permissions.canSeeStudentNames ? r.studentName : null,
        }))
    : null;

  return {
    scale: 5,
    minimumSample: MIN_RATING_SAMPLE,
    structure: { average: structure.length ? sum(structure, (r) => r.score) / structure.length : null, count: structure.length },
    comments,
    table: paginate(
      [
        { key: 'position', label: 'Posição', kind: 'text' },
        { key: 'name', label: 'Professor', kind: 'text', primary: true },
        { key: 'sports', label: 'Esportes', kind: 'text' },
        { key: 'average', label: 'Nota média', kind: 'rating', primary: true },
        { key: 'count', label: 'Avaliações', kind: 'count' },
        { key: 'raters', label: 'Avaliadores únicos', kind: 'count' },
        { key: 'sample', label: 'Ranking', kind: 'status' },
        { key: 'classes', label: 'Aulas realizadas', kind: 'count' },
        { key: 'students', label: 'Alunos atendidos', kind: 'count' },
      ],
      instructorRows(ctx, range, query.sort ?? 'nota'),
      { ...query, sort: query.sort ?? 'nota' },
      { sortOptions: INSTRUCTOR_SORTS, emptyMessage: 'Nenhum professor com esses filtros.' }
    ),
  };
}

// ---------- Pacotes ----------

const CONTRACT_STATUS: Record<Contract['status'], StatusValue> = {
  ativo: status('Ativo', 'success'),
  'renovacao-pendente': status('Renovação pendente', 'accent'),
  vencido: status('Vencido', 'neutral'),
  cancelado: status('Cancelado', 'danger'),
};

function filteredContracts(ctx: AggregateContext, withSpecific: boolean): Contract[] {
  return ctx.db.contracts.filter(
    (contract) =>
      matchesCommon(ctx, contract.unitId, contract.sport) &&
      (!withSpecific ||
        ((!spec(ctx, 'planId') || contract.planId === spec(ctx, 'planId')) &&
          (!spec(ctx, 'contractStatus') || contract.status === spec(ctx, 'contractStatus'))))
  );
}

function overlaps(contract: Contract, range: Range): boolean {
  return contract.start <= range.end && contract.end >= range.start;
}

function usage(ctx: AggregateContext, contract: Contract) {
  const plan = ctx.db.plans.find((p) => p.id === contract.planId)!;
  const random = rng(`${ctx.db.arenaKey}|usage|${contract.id}`);
  const planned = plan.perWeek ? Math.round((daysInclusive(contract.start, contract.end) / 7) * plan.perWeek) : plan.perMonth ?? 0;
  const elapsed = Math.min(1, Math.max(0, daysInclusive(contract.start, ctx.today < contract.end ? ctx.today : contract.end) / daysInclusive(contract.start, contract.end)));
  const scheduled = Math.round(planned * Math.min(1, elapsed + 0.15));
  const used = Math.min(scheduled, Math.round(planned * elapsed * (0.7 + random() * 0.3)));
  const absences = Math.max(0, Math.round(planned * elapsed) - used);
  const makeups = Math.min(absences, Math.floor(random() * 2));
  return { plan, planned, scheduled, used, absences, makeups };
}

export function packageRows(ctx: AggregateContext, range: Range): TableRow[] {
  return filteredContracts(ctx, true)
    .filter((contract) => overlaps(contract, range))
    .sort((a, b) => (a.start < b.start ? 1 : -1))
    .map((contract) => {
      const u = usage(ctx, contract);
      return {
        id: contract.id,
        cells: {
          student: ctx.permissions.canSeeStudentNames ? contract.studentName : `Aluno(a) ${contract.studentId.replace('aluno-', '#')}`,
          plan: u.plan.label,
          kind: u.plan.kind === 'turma-fixa' ? 'Turma fixa' : 'Acesso flexível',
          period: `${formatDate(contract.start)} a ${formatDate(contract.end)}`,
          frequency: u.plan.frequencyLabel,
          planned: u.planned,
          scheduled: u.scheduled,
          used: u.used,
          absences: u.absences,
          makeups: u.makeups,
          status: CONTRACT_STATUS[contract.status],
          payment: contract.paymentPending ? status('Pagamento pendente', 'warning') : status('Em dia', 'success'),
        },
      };
    });
}

export function buildPackages(ctx: AggregateContext, range: Range, query: TableQuery): PackagesBody {
  const prev = previousRange(range);
  const contracts = filteredContracts(ctx, true);
  const stats = (r: Range) => {
    const overlapping = contracts.filter((c) => overlaps(c, r));
    const started = contracts.filter((c) => inRange(c.start, r));
    const usages = overlapping.map((c) => usage(ctx, c));
    return {
      active: overlapping.filter((c) => c.status === 'ativo').length,
      pendingRenewal: overlapping.filter((c) => c.status === 'renovacao-pendente').length,
      expired: contracts.filter((c) => c.status === 'vencido' && inRange(c.end, r)).length,
      cancelled: contracts.filter((c) => c.status === 'cancelado' && inRange(c.end, r)).length,
      sold: started.length,
      renewals: started.filter((c) => c.renewedFrom).length,
      students: new Set(overlapping.map((c) => c.studentId)).size,
      enrollments: overlapping.filter((c) => c.groupId).length,
      pendingPayments: overlapping.filter((c) => c.paymentPending).length,
      used: sum(usages, (u) => u.used),
      planned: sum(usages, (u) => u.planned),
    };
  };
  const s = stats(range);
  const p = stats(prev);
  const base = { dateBasis: 'contrato' as DateBasis };

  const activeByPlan = ctx.db.plans.map((plan) => ({
    id: plan.id,
    label: `${plan.label} · ${plan.frequencyLabel}`,
    value: contracts.filter((c) => overlaps(c, range) && c.planId === plan.id && (c.status === 'ativo' || c.status === 'renovacao-pendente')).length,
  }));

  return {
    metrics: [
      metric(ctx, { ...base, id: 'ativos', label: 'Contratos ativos', help: 'Contratos vigentes no período.', unit: 'count', polarity: 'up' }, s.active, p.active),
      metric(ctx, { ...base, id: 'renovacao', label: 'Renovação pendente', help: 'Vencem em até 7 dias e ainda não renovaram. Não é perda de aluno.', unit: 'count', polarity: 'neutral' }, s.pendingRenewal, undefined),
      metric(ctx, { ...base, id: 'vencidos', label: 'Vencidos sem renovação', help: 'Contratos que terminaram no período e não foram renovados.', unit: 'count', polarity: 'down' }, s.expired, p.expired),
      metric(ctx, { ...base, id: 'cancelados', label: 'Cancelados', help: 'Contratos encerrados por cancelamento.', unit: 'count', polarity: 'down' }, s.cancelled, p.cancelled),
      metric(ctx, { ...base, id: 'vendidos', label: 'Pacotes vendidos', help: 'Contratos iniciados no período, incluindo renovações.', unit: 'count', polarity: 'up' }, s.sold, p.sold),
      metric(ctx, { ...base, id: 'renovacoes', label: 'Renovações', help: 'Contratos iniciados como continuação de um anterior.', unit: 'count', polarity: 'up' }, s.renewals, p.renewals),
      metric(ctx, { ...base, id: 'alunos', label: 'Alunos únicos', help: 'Pessoas com contrato no período. Diferente de matrículas e de participações em aulas.', unit: 'count', polarity: 'up' }, s.students, p.students, { detail: `${s.enrollments} matrículas em turma fixa` }),
      metric(ctx, { ...base, id: 'pagamentos', label: 'Pagamentos pendentes', help: 'Contratos vigentes com cobrança em aberto.', unit: 'count', polarity: 'down' }, s.pendingPayments, p.pendingPayments),
      metric(ctx, { ...base, id: 'utilizacao', label: 'Utilização das aulas', help: 'Aulas usadas sobre aulas previstas nos contratos (agregado). Direito de aula não é crédito financeiro.', unit: 'percent', polarity: 'up' }, pct(s.used, s.planned), pct(p.used, p.planned), { detail: `${s.used} de ${s.planned} aulas previstas` }),
    ],
    byPlan: activeByPlan,
    table: paginate(
      [
        { key: 'student', label: 'Aluno', kind: 'text', primary: true },
        { key: 'plan', label: 'Pacote', kind: 'text', primary: true },
        { key: 'kind', label: 'Tipo', kind: 'text' },
        { key: 'period', label: 'Vigência', kind: 'text' },
        { key: 'frequency', label: 'Frequência contratada', kind: 'text' },
        { key: 'planned', label: 'Previstas', kind: 'count' },
        { key: 'scheduled', label: 'Agendadas', kind: 'count' },
        { key: 'used', label: 'Utilizadas', kind: 'count' },
        { key: 'absences', label: 'Faltas', kind: 'count' },
        { key: 'makeups', label: 'Reposições', kind: 'count' },
        { key: 'status', label: 'Contrato', kind: 'status', primary: true },
        { key: 'payment', label: 'Pagamento', kind: 'status' },
      ],
      packageRows(ctx, range),
      query,
      { emptyMessage: 'Nenhum contrato com esses filtros no período.' }
    ),
  };
}

// ---------- Quadras ----------

export function courtRows(ctx: AggregateContext, range: Range): TableRow[] {
  const slots = courtSlots(ctx, range, true);
  const units = new Map(ctx.db.units.map((u) => [u.id, u.label]));
  return ctx.db.courts
    .filter((court) => slots.some((slot) => slot.courtId === court.id))
    .map((court) => {
      const s = courtStats(slots.filter((slot) => slot.courtId === court.id));
      return {
        id: court.id,
        cells: {
          court: court.label,
          unit: units.get(court.unitId) ?? '—',
          sports: court.sports.join(', '),
          available: s.available,
          blocked: s.blocked,
          confirmed: s.confirmed,
          temporary: s.temporary,
          used: s.used,
          occupancy: { value: s.occupied, capacity: s.available, pct: s.occupancy ?? 0, unit: 'h' },
          cancelled: s.cancelled,
        },
      };
    })
    .sort((a, b) => (b.cells.occupancy as { pct: number }).pct - (a.cells.occupancy as { pct: number }).pct);
}

export function buildCourts(ctx: AggregateContext, range: Range, query: TableQuery): CourtsBody {
  const prev = previousRange(range);
  const slots = courtSlots(ctx, range, true);
  const s = courtStats(slots);
  const p = courtStats(courtSlots(ctx, prev, true));
  const base = { dateBasis: 'reserva' as DateBasis };

  const hourBlocks = [6, 8, 10, 12, 14, 16, 18, 20];
  const weekdays = [1, 2, 3, 4, 5, 6, 0];
  const heatmap: Heatmap = {
    rows: weekdays.map((d) => WEEKDAY_SHORT[d]),
    columns: hourBlocks.map((h) => `${String(h).padStart(2, '0')}h`),
    cells: weekdays.map((day) =>
      hourBlocks.map((hour) => {
        const cell = slots.filter((slot) => weekdayOf(slot.date) === day && slot.hour >= hour && slot.hour < hour + 2);
        const stats = courtStats(cell);
        if (!cell.length || stats.available === 0) {
          const blocked = cell.some((slot) => slot.kind === 'bloqueado');
          return { value: null, state: 'closed' as const, detail: blocked ? 'Bloqueado' : 'Fechado' };
        }
        return { value: stats.occupancy, state: 'ok' as const, detail: `${stats.occupied} de ${stats.available} h ocupadas` };
      })
    ),
  };

  return {
    metrics: [
      metric(ctx, { ...base, id: 'disponiveis', label: 'Horas disponíveis', help: 'Horas de funcionamento sem bloqueio. Horários fechados não contam como vaga.', unit: 'hours', polarity: 'neutral' }, s.available, p.available),
      metric(ctx, { ...base, id: 'reservadas', label: 'Reservas confirmadas', help: 'Horas com reserva paga/confirmada.', unit: 'hours', polarity: 'up' }, s.confirmed, p.confirmed),
      metric(ctx, { ...base, id: 'temporarias', label: 'Reservas temporárias', help: 'Horários segurados aguardando pagamento. Podem expirar e voltar a ficar livres.', unit: 'hours', polarity: 'neutral' }, s.temporary, undefined),
      metric(ctx, { ...base, id: 'uso', label: 'Uso efetivo', help: 'Reservas confirmadas em que os jogadores compareceram.', unit: 'hours', polarity: 'up' }, s.used, p.used),
      metric(ctx, { ...base, id: 'ocupacao', label: 'Ocupação das quadras', help: 'Horas ocupadas (reservas, aulas e torneios, cada hora contada uma vez) sobre horas disponíveis.', unit: 'percent', polarity: 'up' }, s.occupancy, p.occupancy, { detail: `${s.occupied} de ${s.available} h` }),
      metric(ctx, { ...base, id: 'bloqueadas', label: 'Horas bloqueadas', help: 'Manutenção e bloqueios administrativos. Não aparecem como vaga livre.', unit: 'hours', polarity: 'neutral' }, s.blocked, p.blocked),
      metric(ctx, { ...base, id: 'cancelamentos', label: 'Cancelamentos', help: 'Reservas canceladas no período. Aumento é sinal de atenção.', unit: 'count', polarity: 'down' }, s.cancelled, p.cancelled),
    ],
    byCourt: courtRows(ctx, range).map((row) => {
      const ratio = row.cells.occupancy as { value: number; capacity: number; pct: number };
      return { id: row.id, label: String(row.cells.court), value: ratio.value, capacity: ratio.capacity, pct: ratio.pct };
    }),
    mix: [
      { id: 'confirmada', label: 'Reservas confirmadas', value: s.confirmed },
      { id: 'aula', label: 'Aulas', value: s.classes },
      { id: 'torneio', label: 'Torneios', value: s.tournaments },
      { id: 'temporaria', label: 'Temporárias (aguardando pagamento)', value: s.temporary },
      { id: 'livre', label: 'Livres', value: s.available - s.occupied - s.temporary },
    ],
    heatmap,
    table: paginate(
      [
        { key: 'court', label: 'Quadra', kind: 'text', primary: true },
        { key: 'unit', label: 'Unidade', kind: 'text' },
        { key: 'sports', label: 'Esportes', kind: 'text' },
        { key: 'available', label: 'Horas disponíveis', kind: 'count' },
        { key: 'blocked', label: 'Bloqueadas', kind: 'count' },
        { key: 'confirmed', label: 'Confirmadas', kind: 'count' },
        { key: 'temporary', label: 'Temporárias', kind: 'count' },
        { key: 'used', label: 'Uso efetivo', kind: 'count' },
        { key: 'occupancy', label: 'Ocupação', kind: 'ratio', primary: true },
        { key: 'cancelled', label: 'Cancelamentos', kind: 'count' },
      ],
      courtRows(ctx, range),
      query,
      {
        totals: {
          court: 'Todas as quadras filtradas',
          available: s.available,
          blocked: s.blocked,
          confirmed: s.confirmed,
          temporary: s.temporary,
          used: s.used,
          occupancy: { value: s.occupied, capacity: s.available, pct: s.occupancy ?? 0, unit: 'h' },
          cancelled: s.cancelled,
        },
        emptyMessage: 'Nenhuma quadra com esses filtros.',
      }
    ),
  };
}

// ---------- Torneios ----------

const REG_STATUS: Record<RegistrationStatus, string> = {
  confirmada: 'Confirmadas',
  pendente: 'Pendentes',
  cancelada: 'Canceladas',
  'lista-espera': 'Lista de espera',
};

export function buildTournaments(ctx: AggregateContext, range: Range, query: TableQuery): TournamentsBody {
  const prev = previousRange(range);
  const tournaments = ctx.db.tournaments.filter(
    (t) => matchesCommon(ctx, t.unitId, t.sport) && (!spec(ctx, 'tournamentId') || t.id === spec(ctx, 'tournamentId'))
  );
  const regs = (r: Range) =>
    ctx.db.registrations.filter(
      (reg) =>
        tournaments.some((t) => t.id === reg.tournamentId) &&
        (!spec(ctx, 'category') || reg.categoryId === spec(ctx, 'category')) &&
        (!spec(ctx, 'registrationStatus') || reg.status === spec(ctx, 'registrationStatus')) &&
        inRange(reg.registeredAt, r)
    );
  const current = regs(range);
  const previous = regs(prev);
  const count = (list: typeof current, st: RegistrationStatus) => list.filter((reg) => reg.status === st).length;
  const base = { dateBasis: 'inscricao' as DateBasis };

  const categories = tournaments.flatMap((t) =>
    t.categories
      .filter((c) => !spec(ctx, 'category') || c.id === spec(ctx, 'category'))
      .map((category) => {
        const list = current.filter((reg) => reg.categoryId === category.id);
        const filled = list.filter((reg) => reg.status === 'confirmada' || reg.status === 'pendente').length;
        const unitLabel = t.capacityUnit === 'dupla' ? 'duplas' : 'jogadores';
        return {
          tournament: t,
          category,
          list,
          filled,
          unitLabel,
          bar: { id: category.id, label: `${t.label} · ${category.label}`, value: filled, capacity: category.capacity, pct: pct(filled, category.capacity) ?? 0, note: `Capacidade em ${unitLabel}` },
        };
      })
  );

  const rows: TableRow[] = categories
    .filter((item) => item.list.length)
    .map((item) => ({
      id: item.category.id,
      cells: {
        tournament: item.tournament.label,
        category: item.category.label,
        capacityUnit: item.tournament.capacityUnit === 'dupla' ? 'Dupla' : 'Jogador',
        filled: { value: item.filled, capacity: item.category.capacity, pct: item.bar.pct, unit: item.unitLabel },
        integral: item.list.filter((reg) => reg.paymentMode === 'integral' && reg.status === 'confirmada').length,
        split: item.list.filter((reg) => reg.paymentMode === 'dividido' && reg.status === 'confirmada').length,
        pending: count(item.list, 'pendente'),
        cancelled: count(item.list, 'cancelada'),
        waitlist: count(item.list, 'lista-espera'),
        received: sum(item.list, (reg) => reg.paidCents),
      },
    }));

  const filledCurrent = categories.reduce((total, item) => total + item.filled, 0);
  const capacity = categories.reduce((total, item) => total + item.category.capacity, 0);

  return {
    metrics: [
      metric(ctx, { ...base, id: 'confirmadas', label: 'Inscrições confirmadas', help: 'Inscrições pagas e confirmadas. Uma dupla com pagamento dividido conta como uma inscrição.', unit: 'count', polarity: 'up' }, count(current, 'confirmada'), count(previous, 'confirmada')),
      metric(ctx, { ...base, id: 'preenchimento', label: 'Vagas preenchidas', help: 'Confirmadas + pendentes sobre a capacidade das categorias (cada categoria tem sua unidade: dupla ou jogador).', unit: 'percent', polarity: 'up' }, pct(filledCurrent, capacity), undefined, { detail: `${filledCurrent} de ${capacity} vagas` }),
      metric(ctx, { ...base, id: 'pendentes', label: 'Pagamentos pendentes', help: 'Inscrições aguardando pagamento (total ou da segunda cota).', unit: 'count', polarity: 'neutral' }, count(current, 'pendente'), count(previous, 'pendente')),
      metric(ctx, { ...base, id: 'recebido', label: 'Valor recebido', help: 'Pago pelas inscrições no período (bruto).', unit: 'money', polarity: 'up' }, sum(current, (reg) => reg.paidCents), sum(previous, (reg) => reg.paidCents)),
      metric(ctx, { ...base, id: 'canceladas', label: 'Cancelamentos', help: 'Inscrições canceladas. Aumento é sinal de atenção.', unit: 'count', polarity: 'down' }, count(current, 'cancelada'), count(previous, 'cancelada')),
      metric(ctx, { ...base, id: 'espera', label: 'Lista de espera', help: 'Inscrições aguardando vaga.', unit: 'count', polarity: 'neutral' }, count(current, 'lista-espera'), count(previous, 'lista-espera')),
    ],
    categories: categories.map((item) => item.bar),
    table: paginate(
      [
        { key: 'tournament', label: 'Torneio', kind: 'text', primary: true },
        { key: 'category', label: 'Categoria', kind: 'text', primary: true },
        { key: 'capacityUnit', label: 'Unidade da vaga', kind: 'text' },
        { key: 'filled', label: 'Inscritos / capacidade', kind: 'ratio', primary: true },
        { key: 'integral', label: 'Pagas integralmente', kind: 'count' },
        { key: 'split', label: 'Pagas divididas', kind: 'count' },
        { key: 'pending', label: REG_STATUS.pendente, kind: 'count' },
        { key: 'cancelled', label: REG_STATUS.cancelada, kind: 'count' },
        { key: 'waitlist', label: REG_STATUS['lista-espera'], kind: 'count' },
        { key: 'received', label: 'Recebido', kind: 'money' },
      ],
      rows,
      query,
      {
        totals: { tournament: 'Total', received: sum(current, (reg) => reg.paidCents) },
        emptyMessage: 'Nenhuma inscrição com esses filtros no período.',
      }
    ),
  };
}
